import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Loader2,
  Mail,
  MessageSquare,
  Printer,
  RefreshCw,
  Send,
  Trash2,
  Upload,
} from 'lucide-react';

import { ReturnPlanView, ReturnResultView, type ReturnPlan, type ReturnResult } from '@/components/admin/ReturnBooking';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InfoDot } from '@/components/ui/InfoDot';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { adminFetch } from '@/hooks/admin/useAdminApi';
import {
  CASE_STATUS_LABEL,
  useCaseAction,
  usePaymentCase,
  type CaseDocument,
  type CaseEvent,
  type CaseStatus,
  type PaymentCase,
} from '@/hooks/admin/usePaymentCases';

/**
 * AdminPaymentCase — /admin/payments/cases/:caseId: one payment ticket.
 *
 * Opened from the Returned payments queue. The ticket collected the invoices
 * the payment covered and their delivery signatures and photos; here a person
 * reads and edits the drafted email and letter, ticks which documents go with
 * them, previews, then emails and/or mails the packet (DocuPost, like
 * statements). The Stripe response text is kept here to paste into Stripe.
 *
 * "Books" puts the money right in QuickBooks: the payment is unapplied from
 * every invoice it paid (they reopen), the clawback is booked against the
 * customer, the fee invoice is created, ordering pauses — and for a Stripe
 * dispute the payout that netted the chargeback posts.
 */

type Editable = Pick<
  PaymentCase,
  'email_to' | 'email_subject' | 'email_body' | 'letter_title' | 'letter_body' | 'stripe_response' | 'reply_by' | 'evidence_due_by'
>;
const FIELDS: Array<keyof Editable> = [
  'email_to',
  'email_subject',
  'email_body',
  'letter_title',
  'letter_body',
  'stripe_response',
  'reply_by',
  'evidence_due_by',
];

const textareaCls =
  'mt-1 w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink placeholder:text-brix-muted focus:outline-none focus:ring-2 focus:ring-brix-primary/40';
const labelCls = 'text-[11px] font-semibold text-brix-muted uppercase tracking-tight';

function pickEditable(c: PaymentCase): Editable {
  const out = {} as Editable;
  for (const k of FIELDS) (out as Record<string, string | null>)[k] = c[k] ?? '';
  return out;
}

function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function eventText(e: CaseEvent): string {
  const d = e.detail ?? {};
  switch (e.action) {
    case 'created':
      return 'Ticket opened';
    case 'documents_collected': {
      const errs = Array.isArray(d.errors) ? d.errors.length : 0;
      return `Documents collected (${Number(d.added ?? 0)} new)${errs ? `, ${errs} could not be fetched` : ''}`;
    }
    case 'status':
      return `Status: ${CASE_STATUS_LABEL[d.from as CaseStatus] ?? d.from} → ${CASE_STATUS_LABEL[d.to as CaseStatus] ?? d.to}`;
    case 'email_sent':
      return `Emailed ${Array.isArray(d.to) ? d.to.join(', ') : ''} with ${Array.isArray(d.attachments) ? d.attachments.length : 0} attachments`;
    case 'letter_mailed':
      return d.ok ? `Letter mailed (${Number(d.pages ?? 0)} pages)` : 'Letter mailing FAILED at DocuPost';
    case 'document_added':
      return `Uploaded: ${String(d.label ?? '')}`;
    case 'document_removed':
      return `Removed: ${String(d.label ?? '')}`;
    case 'updated':
      return 'Edited';
    case 'note':
      return String(d.text ?? '');
    case 'booked':
      return `Booked in QuickBooks: reopened ${Array.isArray(d.reopened) ? d.reopened.map((x) => `#${String(x)}`).join(', ') : 'the invoices'}` +
        (d.fee_invoice ? `; fee invoice #${String(d.fee_invoice)} (${formatUsd(Number(d.fee_amount ?? 0))})` : '');
    default:
      return e.action;
  }
}

async function fileToBase64(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

export default function AdminPaymentCase() {
  const { caseId } = useParams<{ caseId: string }>();
  const detail = usePaymentCase(caseId);
  const act = useCaseAction<Record<string, unknown>>();
  const [form, setForm] = useState<Editable | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [emailPreview, setEmailPreview] = useState<{ to: string[]; attachments: string[]; bytes: number } | null>(null);
  const [letterPreview, setLetterPreview] = useState<{ url: string; pages?: number; skipped?: string[] } | null>(null);
  const [color, setColor] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const c = detail.data?.case;
  // Load the server copy into the form once (and after a redraft).
  useEffect(() => {
    if (c && form === null) setForm(pickEditable(c));
  }, [c, form]);

  const dirtyFields = useMemo(() => {
    if (!c || !form) return [] as Array<keyof Editable>;
    return FIELDS.filter((k) => (form[k] ?? '') !== (c[k] ?? ''));
  }, [c, form]);

  if (detail.isLoading || !form) {
    return (
      <p className="text-sm text-brix-muted p-6">
        <Loader2 size={14} className="animate-spin inline mr-1.5" />
        Loading ticket…
      </p>
    );
  }
  if (detail.isError || !c) {
    return <p className="text-sm text-red-700 p-6">{detail.error instanceof Error ? detail.error.message : 'Ticket not found'}</p>;
  }

  const docs = detail.data!.documents;
  const events = detail.data!.events;

  const set = (k: keyof Editable) => (v: string) => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
    if (k.startsWith('email')) setEmailPreview(null);
    if (k.startsWith('letter')) setLetterPreview(null);
  };

  async function run<T>(label: string, body: Record<string, unknown>, ok?: (r: T) => string | null): Promise<T | null> {
    setBusy(label);
    setNotice(null);
    try {
      const r = (await act.mutateAsync({ id: c!.id, ...body })) as T;
      const msg = ok?.(r);
      if (msg) setNotice({ kind: 'ok', text: msg });
      return r;
    } catch (err) {
      setNotice({ kind: 'err', text: err instanceof Error ? err.message : String(err) });
      return null;
    } finally {
      setBusy(null);
    }
  }

  /** Save edited fields first, so a send always uses what is on screen. */
  async function save(): Promise<boolean> {
    if (dirtyFields.length === 0) return true;
    const fields: Record<string, string> = {};
    for (const k of dirtyFields) fields[k] = (form![k] ?? '') as string;
    const r = await run('save', { action: 'update', fields });
    return r !== null;
  }

  const included = docs.filter((d) => d.include);
  const daysLeft = c.evidence_due_by
    ? Math.ceil((new Date(`${c.evidence_due_by}T23:59:00-08:00`).getTime() - Date.now()) / 86_400_000)
    : null;

  return (
    <div className="space-y-5">
      <div>
        <Link to="/admin/payments?returns=1" className="inline-flex items-center gap-1 text-xs font-semibold text-brix-primary hover:underline">
          <ArrowLeft size={12} /> Payments
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-brix-ink">
              {c.label} · {c.customer_name ?? 'Unmatched customer'}
            </h1>
            <p className="text-sm text-brix-muted mt-0.5">
              {c.amount != null ? formatUsd(Number(c.amount)) : '—'}{' '}
              {c.stripe_dispute_id ? 'disputed' : 'returned'}
              {c.reason_code ? ` · bank reason: ${c.reason_code.replace(/_/g, ' ')}` : ''}
              {c.stripe_dispute_id && (
                <>
                  {' · '}
                  <a
                    href={`https://dashboard.stripe.com/disputes/${c.stripe_dispute_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brix-primary hover:underline inline-flex items-center gap-0.5"
                  >
                    open in Stripe <ExternalLink size={11} />
                  </a>
                </>
              )}
              {c.customer_id && (
                <>
                  {' · '}
                  <Link to={`/admin/customers/${c.customer_id}/billing`} className="text-brix-primary hover:underline">
                    customer billing
                  </Link>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className={labelCls} htmlFor="case-status">Status</label>
            <select
              id="case-status"
              value={c.status}
              disabled={busy !== null}
              onChange={(e) => run('status', { action: 'update', fields: { status: e.target.value } })}
              className="rounded-md border border-brix-border bg-brix-surface px-2 py-1.5 text-sm text-brix-ink"
            >
              {(Object.keys(CASE_STATUS_LABEL) as CaseStatus[]).map((s) => (
                <option key={s} value={s}>{CASE_STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {notice && (
        <div
          className={cn(
            'rounded-lg border px-3 py-2 text-sm',
            notice.kind === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800',
          )}
        >
          {notice.text}
        </div>
      )}

      {/* Dates */}
      <div className="rounded-xl border border-brix-border bg-brix-surface p-4 grid gap-4 sm:grid-cols-3">
        <div>
          <label className={labelCls}>Customer reply by</label>
          <Input type="date" value={form.reply_by ?? ''} onChange={(e) => set('reply_by')(e.target.value)} className="mt-1" />
          <p className="text-[11px] text-brix-muted mt-1">The date the email and letter ask them to answer by. Change it here, then edit the wording to match.</p>
        </div>
        <div>
          <label className={labelCls}>Stripe evidence due</label>
          <Input type="date" value={form.evidence_due_by ?? ''} onChange={(e) => set('evidence_due_by')(e.target.value)} className="mt-1" />
          {daysLeft != null && (
            <p className={cn('text-[11px] mt-1 font-semibold', daysLeft <= 3 ? 'text-red-700' : 'text-brix-muted')}>
              {daysLeft > 0 ? `${daysLeft} days left` : daysLeft === 0 ? 'Due today' : 'Past due: Stripe decides without our evidence'}
            </p>
          )}
        </div>
        <div className="text-xs text-brix-muted self-end">
          Opened {fmtWhen(c.created_at)} by {c.created_by ?? 'admin'}
        </div>
      </div>

      <BooksCard
        ret={detail.data!.payment_return}
        isDispute={!!c.stripe_dispute_id}
        onBooked={() => void detail.refetch()}
      />

      {/* Documents */}
      <section className="rounded-xl border border-brix-border bg-brix-surface p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-brix-ink flex items-center gap-1.5">
            Documents
            <span className="text-xs font-normal text-brix-muted">{included.length} of {docs.length} go with the email and letter</span>
            <InfoDot text="Collected when the ticket opened: the invoices this payment covered (printed as issued) and the delivery signatures and photos from Service Fusion. Tick what to send. Upload anything else, like texts or emails with the customer (PDF or image to be mailable)." />
          </h2>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy !== null}
              onClick={() =>
                run<{ documents: number; errors: string[] }>('collect', { action: 'collect' }, (r) =>
                  r.errors.length ? `Collected ${r.documents} new. Could not fetch: ${r.errors.join('; ')}` : `Collected ${r.documents} new documents.`,
                )
              }
            >
              {busy === 'collect' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Collect again
            </Button>
            <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => fileRef.current?.click()}>
              {busy === 'upload' ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Upload
            </Button>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.eml"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                if (file.size > 4_000_000) {
                  setNotice({ kind: 'err', text: 'That file is over 4 MB. Save it smaller (or as a PDF) and try again.' });
                  return;
                }
                await run('upload', {
                  action: 'upload',
                  filename: file.name,
                  content_type: file.type || 'application/octet-stream',
                  content_base64: await fileToBase64(file),
                }, () => `Uploaded ${file.name}.`);
              }}
            />
          </div>
        </div>
        {docs.length === 0 ? (
          <p className="text-sm text-brix-muted">No documents yet. Upload the evidence, or Collect again once the payment is matched.</p>
        ) : (
          <ul className="divide-y divide-brix-border rounded-lg border border-brix-border">
            {docs.map((d) => (
              <DocLine
                key={d.id}
                doc={d}
                disabled={busy !== null}
                onToggle={(include) => run('doc', { action: 'document', doc_id: d.id, include })}
                onRemove={() => {
                  if (!window.confirm(d.kind === 'upload' ? `Delete ${d.label}?` : `Take ${d.label} off the ticket? Collect again puts it back.`)) return;
                  run('doc', { action: 'document', doc_id: d.id, remove: true });
                }}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Email */}
      <section className="rounded-xl border border-brix-border bg-brix-surface p-4 space-y-3">
        <h2 className="text-base font-semibold text-brix-ink flex items-center gap-1.5">
          <Mail size={15} className="text-brix-primary" /> Email the customer
        </h2>
        <div>
          <label className={labelCls}>To</label>
          <Input value={form.email_to ?? ''} onChange={(e) => set('email_to')(e.target.value)} placeholder="billing@customer.com, second@customer.com" className="mt-1" />
        </div>
        <div>
          <label className={labelCls}>Subject</label>
          <Input value={form.email_subject ?? ''} onChange={(e) => set('email_subject')(e.target.value)} className="mt-1" />
        </div>
        <div>
          <label className={labelCls}>Message</label>
          <textarea value={form.email_body ?? ''} onChange={(e) => set('email_body')(e.target.value)} rows={16} className={textareaCls} />
          <p className="text-[11px] text-brix-muted mt-1">Lines starting with "- " become a list. Replies go to service@brixbev.com, which also gets a copy.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            onClick={async () => {
              if (!(await save())) return;
              const r = await run<{ to: string[]; attachments: string[]; bytes: number }>('email-preview', { action: 'email', mode: 'preview' });
              if (r) setEmailPreview(r);
            }}
          >
            {busy === 'email-preview' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            Check before sending
          </Button>
          <Button
            size="sm"
            disabled={busy !== null || !emailPreview}
            title={emailPreview ? undefined : 'Check before sending first'}
            onClick={async () => {
              if (!window.confirm(`Email ${emailPreview!.to.join(', ')} with ${emailPreview!.attachments.length} attachments? This goes to the customer now.`)) return;
              if (!(await save())) return;
              const r = await run('email-send', { action: 'email', mode: 'send' }, () => 'Email sent.');
              if (r) setEmailPreview(null);
            }}
          >
            {busy === 'email-send' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Send email
          </Button>
        </div>
        {emailPreview && (
          <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2 text-xs text-brix-ink space-y-1">
            <p><strong>To:</strong> {emailPreview.to.join(', ')}</p>
            <p><strong>Attachments ({emailPreview.attachments.length}, {(emailPreview.bytes / 1_000_000).toFixed(1)} MB):</strong></p>
            <ul className="list-disc pl-5">{emailPreview.attachments.map((a) => <li key={a}>{a}</li>)}</ul>
          </div>
        )}
      </section>

      {/* Letter */}
      <section className="rounded-xl border border-brix-border bg-brix-surface p-4 space-y-3">
        <h2 className="text-base font-semibold text-brix-ink flex items-center gap-1.5">
          <Printer size={15} className="text-brix-primary" /> Mail a letter
          <span className="text-xs font-normal text-brix-muted">DocuPost · USPS first class, to the QuickBooks billing address · the ticked PDFs and photos are enclosed</span>
        </h2>
        <div>
          <label className={labelCls}>Title</label>
          <Input value={form.letter_title ?? ''} onChange={(e) => set('letter_title')(e.target.value)} className="mt-1" />
        </div>
        <div>
          <label className={labelCls}>Letter</label>
          <textarea value={form.letter_body ?? ''} onChange={(e) => set('letter_body')(e.target.value)} rows={16} className={textareaCls} />
        </div>
        <label className="flex items-center gap-2 text-xs text-brix-ink">
          <input type="checkbox" checked={color} onChange={(e) => { setColor(e.target.checked); setLetterPreview(null); }} />
          Print in color (for the photos; costs more)
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            onClick={async () => {
              if (!(await save())) return;
              const r = await run<{ pdf_url: string; pages?: number; skipped?: string[] }>('letter-preview', { action: 'mail', mode: 'preview', color });
              if (r) {
                setLetterPreview({ url: r.pdf_url, pages: r.pages, skipped: r.skipped });
                window.open(r.pdf_url, '_blank', 'noopener');
              }
            }}
          >
            {busy === 'letter-preview' ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
            Preview the packet
          </Button>
          <Button
            size="sm"
            disabled={busy !== null || !letterPreview}
            title={letterPreview ? undefined : 'Preview the packet first'}
            onClick={async () => {
              if (!window.confirm(`Mail this ${letterPreview!.pages ?? ''}-page packet to ${c.customer_name ?? 'the customer'} via DocuPost? This spends real postage and cannot be undone.`)) return;
              if (!(await save())) return;
              const r = await run<{ ok: boolean }>('letter-send', { action: 'mail', mode: 'send', color }, (x) => (x.ok ? 'Letter mailed.' : null));
              if (r) setLetterPreview(null);
            }}
          >
            {busy === 'letter-send' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Mail it
          </Button>
          {letterPreview && (
            <a href={letterPreview.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-brix-primary hover:underline">
              <ExternalLink size={12} /> Open preview ({letterPreview.pages} pages)
            </a>
          )}
        </div>
        {letterPreview?.skipped && letterPreview.skipped.length > 0 && (
          <p className="text-xs text-amber-800">Not in the envelope (only PDFs and JPG/PNG can be mailed): {letterPreview.skipped.join(', ')}</p>
        )}
      </section>

      {/* Stripe response */}
      <section className="rounded-xl border border-brix-border bg-brix-surface p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-brix-ink">Stripe dispute response</h2>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                await navigator.clipboard.writeText(form.stripe_response ?? '');
                setNotice({ kind: 'ok', text: 'Copied. Paste it into the dispute in Stripe and attach the same documents.' });
              }}
            >
              <Copy size={12} /> Copy
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy !== null}
              onClick={async () => {
                if (!window.confirm('Rewrite the email, letter and Stripe text from the payment facts? Your edits to them will be replaced.')) return;
                const r = await run('redraft', { action: 'redraft' }, () => 'Drafts rewritten.');
                if (r) {
                  // Load the rewritten text, not the copy cached before it.
                  const fresh = await detail.refetch();
                  if (fresh.data?.case) setForm(pickEditable(fresh.data.case));
                }
              }}
            >
              <RefreshCw size={12} /> Rewrite drafts
            </Button>
          </div>
        </div>
        <textarea value={form.stripe_response ?? ''} onChange={(e) => set('stripe_response')(e.target.value)} rows={12} className={textareaCls} />
        <p className="text-[11px] text-brix-muted">Fill in how the customer answered before you submit. Submitting in Stripe is final, so it is done there by a person.</p>
      </section>

      {dirtyFields.length > 0 && (
        <div className="sticky bottom-3 z-10 flex justify-end">
          <div className="rounded-lg border border-brix-border bg-brix-surface shadow-md px-3 py-2 flex items-center gap-3 text-sm">
            <span className="text-brix-muted">Unsaved changes</span>
            <Button size="sm" disabled={busy !== null} onClick={async () => { if (await save()) setNotice({ kind: 'ok', text: 'Saved.' }); }}>
              {busy === 'save' ? <Loader2 size={12} className="animate-spin" /> : null}
              Save
            </Button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <section className="rounded-xl border border-brix-border bg-brix-surface p-4 space-y-3">
        <h2 className="text-base font-semibold text-brix-ink flex items-center gap-1.5">
          <MessageSquare size={15} className="text-brix-primary" /> Timeline
        </h2>
        <div className="flex gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note: e.g. called Hector, he says the bank will withdraw it" />
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null || !note.trim()}
            onClick={async () => {
              const r = await run('note', { action: 'note', text: note.trim() });
              if (r) setNote('');
            }}
          >
            Add
          </Button>
        </div>
        <ul className="space-y-1.5">
          {events.map((e) => (
            <li key={e.id} className="text-sm flex gap-3">
              <span className="text-xs text-brix-muted whitespace-nowrap w-32 shrink-0">{fmtWhen(e.created_at)}</span>
              <span className={cn('text-brix-ink', e.action === 'note' && 'italic')}>
                {eventText(e)}
                <span className="text-xs text-brix-muted"> · {e.actor ?? 'system'}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function DocLine({
  doc,
  disabled,
  onToggle,
  onRemove,
}: {
  doc: CaseDocument;
  disabled: boolean;
  onToggle: (include: boolean) => void;
  onRemove: () => void;
}) {
  const Icon = doc.content_type.startsWith('image/') ? ImageIcon : FileText;
  return (
    <li className={cn('flex items-center gap-3 px-3 py-2', !doc.include && 'opacity-60')}>
      <input type="checkbox" checked={doc.include} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} aria-label={`Send ${doc.label}`} />
      <Icon size={14} className="text-brix-muted shrink-0" />
      <span className="flex-1 text-sm text-brix-ink">
        {doc.label}
        {!doc.mailable && <span className="ml-2 text-[11px] text-amber-800">email only (not mailable)</span>}
      </span>
      {doc.url && (
        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-brix-primary hover:underline">
          Open
        </a>
      )}
      <button type="button" onClick={onRemove} disabled={disabled} className="text-brix-muted hover:text-red-700" title="Remove">
        <Trash2 size={14} />
      </button>
    </li>
  );
}

interface BookedReturn {
  id: string;
  booked_at: string | null;
  booked_by: string | null;
  booking_channel: string | null;
  qbo_payment_id: string | null;
  qbo_return_expense_id: string | null;
  qbo_fee_doc_number: string | null;
  fee_amount: number | string | null;
  reopened_invoices: Array<{ id: string; doc_number: string; applied: number }> | null;
  reinstated_at: string | null;
  payment_id: string | null;
}

/**
 * Books — put the returned / disputed payment right in QuickBooks, from here.
 * Preview first; nothing is written until "Book in QuickBooks".
 */
function BooksCard({
  ret,
  isDispute,
  onBooked,
}: {
  ret: Record<string, unknown> | null;
  isDispute: boolean;
  onBooked: () => void;
}) {
  const r = ret as unknown as BookedReturn | null;
  const [fee, setFee] = useState(isDispute ? '50' : '35');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [pauseOrdering, setPauseOrdering] = useState(true);
  const [busy, setBusy] = useState<'preview' | 'record' | null>(null);
  const [plan, setPlan] = useState<ReturnPlan | null>(null);
  const [result, setResult] = useState<ReturnResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!r) return null;

  const payload = (mode: 'preview' | 'record') => ({
    return_id: r.id,
    mode,
    fee_amount: Number(fee) || 0,
    hold: pauseOrdering,
    notify: notifyCustomer,
  });

  async function go(mode: 'preview' | 'record') {
    if (mode === 'record' && plan) {
      const ok = window.confirm(
        `Book this in QuickBooks: unapply payment ${plan.payment.id} (${formatUsd(plan.payment.total)}) and reopen ` +
          `${plan.reopens.map((i) => `#${i.doc_number}`).join(', ')}` +
          (Number(fee) > 0 ? `, and invoice a ${formatUsd(Number(fee))} fee?` : '?') +
          ' This writes to QuickBooks and cannot be undone from here.',
      );
      if (!ok) return;
    }
    setBusy(mode);
    setError(null);
    try {
      if (mode === 'preview') {
        setResult(null);
        const res = await adminFetch<{ plan: ReturnPlan }>('admin-record-returned-payment', {
          method: 'POST',
          body: JSON.stringify(payload('preview')),
        });
        setPlan(res.plan);
      } else {
        const res = await adminFetch<ReturnResult>('admin-record-returned-payment', {
          method: 'POST',
          body: JSON.stringify(payload('record')),
        });
        setResult(res);
        setPlan(null);
        onBooked();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="rounded-xl border border-brix-border bg-brix-surface p-4 space-y-3">
      <h2 className="text-base font-semibold text-brix-ink flex items-center gap-1.5">
        Books
        <InfoDot text="Puts the money right in QuickBooks. The payment is unapplied from every invoice it paid, so they show unpaid again; the clawback is booked against the customer; a fee invoice is created; ordering pauses. For a Stripe dispute, the payout Stripe took the money out of then posts by itself. If we win the dispute, the money is re-applied to these invoices automatically when Stripe pays it back." />
      </h2>

      {r.booked_at ? (
        <div className="text-sm text-brix-ink space-y-1">
          <p className="text-emerald-700 font-semibold">
            Booked in QuickBooks {fmtWhen(r.booked_at)} by {r.booked_by ?? 'admin'}.
          </p>
          <p className="text-xs text-brix-muted">
            Payment {r.qbo_payment_id} unapplied; reopened{' '}
            {(r.reopened_invoices ?? []).map((i) => `#${i.doc_number} (${formatUsd(Number(i.applied))})`).join(', ') || 'its invoices'}.
            {r.qbo_fee_doc_number ? ` Fee invoice #${r.qbo_fee_doc_number} (${formatUsd(Number(r.fee_amount ?? 0))}).` : ' No fee.'}
            {r.booking_channel === 'stripe' ? ' Clawback booked through Stripe Disputes Clearing.' : ''}
          </p>
          {r.reinstated_at && (
            <p className="text-xs text-emerald-700">
              Dispute won: Stripe paid the money back and it was re-applied to these invoices {fmtWhen(r.reinstated_at)}.
            </p>
          )}
        </div>
      ) : (
        <>
          <p className="text-xs text-brix-muted">
            Not booked yet: QuickBooks still shows these invoices as paid
            {isDispute ? ', and the Stripe payout that took the money back is held until this is done.' : '.'}
          </p>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelCls}>{isDispute ? 'Dispute fee' : 'Return fee'} ($)</label>
              <Input value={fee} onChange={(e) => { setFee(e.target.value); setPlan(null); }} type="number" min={0} className="mt-1 w-28" />
            </div>
            <label className="flex items-center gap-2 text-xs text-brix-ink cursor-pointer">
              <input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} className="accent-brix-primary" />
              Email the customer
            </label>
            <label className="flex items-center gap-2 text-xs text-brix-ink cursor-pointer">
              <input type="checkbox" checked={pauseOrdering} onChange={(e) => setPauseOrdering(e.target.checked)} className="accent-brix-primary" />
              Pause ordering (credit hold)
            </label>
            <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => void go('preview')}>
              {busy === 'preview' ? <Loader2 size={12} className="animate-spin" /> : null}
              Preview
            </Button>
            {plan && (
              <Button
                variant="primary"
                size="sm"
                disabled={busy !== null}
                onClick={() => void go('record')}
                className="bg-red-600 hover:bg-red-700 text-red-50"
              >
                {busy === 'record' ? <Loader2 size={12} className="animate-spin" /> : null}
                Book in QuickBooks
              </Button>
            )}
          </div>
          {plan && <ReturnPlanView plan={plan} pauseOrdering={pauseOrdering} notifyCustomer={notifyCustomer} />}
        </>
      )}
      {result && <ReturnResultView result={result} />}
      {error && <p className="text-sm text-red-700">{error}</p>}
    </section>
  );
}
